import { NgClass } from "@angular/common";
import {
    Component,
    forwardRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    ChangeDetectionStrategy
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbCalendar, NgbDate, NgbDateStruct, NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { isDate } from "lodash";
import moment from "moment";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { RequiredDirective } from "../../directives/required/required.directive";
import { ValidationMessageComponent } from "../../validation/validation-message/validation-message.component";
import { ControlValueAccessorBaseComponent } from "../base-component/control-value-accessor-base.component";
import { InfoComponent } from "../info/info.component";
import { DatepickerService } from "./datepicker.service";
import { Month } from "./month";
import { Week } from "./week";

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatepickerComponent)
        },
        {
            multi: false,
            provide: ControlValueAccessorBaseComponent,
            useExisting: forwardRef(() => DatepickerComponent)
        }
    ],
    selector: "rask-datepicker",
    styleUrls: ["./datepicker.component.scss"],
    templateUrl: "./datepicker.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RequiredDirective,
        InfoComponent,
        NgClass,
        NgbPopover,
        FormsModule,
        ValidationMessageComponent
    ]
})
export class DatepickerComponent extends ControlValueAccessorBaseComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() allowInvalidInput: boolean;
    @Input() appendToBody = true;
    currentDateIndex = 0;
    @Input() formatter: (i: NgbDateStruct) => string;
    isFocused = false;
    localMaxDate: NgbDateStruct;
    localMinDate: NgbDateStruct;
    months: Month[];
    @Input() onlyMonths = false;
    placeholder = "YYYY-MM-DD";
    selectedDate: NgbDate;
    startDate: NgbDate;
    viewDate: NgbDate;
    weekDates: NgbDate[];
    weekdays: string[];
    weeks: Week[];

    private readonly calendar = inject(NgbCalendar);
    private readonly service = inject(DatepickerService);

    get inputValue() {
        return this.service.toDisplayText(this.privateValue, this.onlyMonths);
    }

    get maxDate() {
        return this.localMaxDate;
    }

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("maxDate")
    set maxDate(value: any) {
        this.localMaxDate = this.service.parseMaxDate(value, this.onlyMonths);
    }

    get minDate() {
        return this.localMinDate;
    }

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("minDate")
    set minDate(value: any) {
        this.localMinDate = this.service.parseMinDate(value, this.onlyMonths);
    }

    constructor() {
        super();
        this.formatter = this.service.toTextFormatter;
    }

    clear() {
        this.writeValue("");
        this.triggerChanges();
    }

    close() {
        this.isFocused = false;
    }

    getCellStyle(date: NgbDate) {
        const classList = [];
        if (!this.onlyMonths && date.month !== this.viewDate.month) {
            classList.push("outside");
        }
        if (date.equals(this.value)) {
            classList.push("selected");
        }
        if (this.dateOutOfRange(date)) {
            classList.push("disabled");
        }

        return classList;
    }

    getMonthName() {
        return this.service.getMonthNames()[this.viewDate.month - 1];
    }

    init(viewDate: NgbDate) {
        this.viewDate = viewDate;
        this.startDate = this.service.getStartDate(this.viewDate);
        this.weekdays = this.service.getWeekDayNames().map(d => d.substring(0, 2));
        this.weeks = this.service.getMonthWeeks(this.startDate);
        this.months = this.service.getMonths(this.viewDate);
    }

    manualEdit(value) {
        if (!ObjectUtils.stringHasValue(value)) {
            this.clear();
        }
        const date = this.onlyMonths ? moment(value, "YYYY-MM", true) : moment(value, DateUtils.dateformat, true);
        if (!date.isValid()) {
            return;
        }

        const parsedDate = this.service.parseTextDate(value, this.onlyMonths);
        this.selectDate(parsedDate, true);
    }

    nextMonth() {
        this.init(this.calendar.getNext(this.viewDate, "m", 1));
    }

    nextYear() {
        this.init(this.calendar.getNext(this.viewDate, "y", 1));
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.onlyMonths) {
            this.placeholder = "YYYY-MM";
        }
    }

    onBlur() {
        this.isFocused = false;
    }

    onFocus() {
        this.isFocused = true;
    }

    prevMonth() {
        this.init(this.calendar.getPrev(this.viewDate, "m", 1));
    }

    prevYear() {
        this.init(this.calendar.getPrev(this.viewDate, "y", 1));
    }

    selectDate(date: NgbDate, fromManEdit = false) {
        if (date !== undefined && (date.before(this.minDate) || date.after(this.maxDate)) && (!fromManEdit || !this.allowInvalidInput)) {
            return;
        }
        this.value = date;
    }

    toggle(popover: NgbPopover) {
        if (this.disabled) {
            return;
        }

        if (popover.isOpen()) {
            popover.close();
            return;
        }

        const showDate = this.value ? this.value : this.calendar.getToday();
        this.init(showDate);
        popover.open();
    }

    writeValue(obj: any): void {
        if (obj === undefined) {
            return;
        }

        let valueToParse = obj;
        if (isDate(obj)) {
            valueToParse = obj.toString();
        }
        const parsed = this.service.parseTextDate(valueToParse);
        this.privateValue = parsed;
    }

    private dateOutOfRange(date: NgbDate) {
        return this.minDate && date.before(this.minDate) || this.maxDate && date.after(this.maxDate);
    }
}
